/* eslint-disable */
import type { MarketCapability } from "../cash/cashabilities/api/model.pb";
import type {
  AutomationAutomationStatus,
  AutomationAutomationTarget,
} from "../cash/investautomator/model/automation.pb";
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { CurrencyCode } from "../common/currency_codes.pb";
import type { InstrumentType } from "../common/instrument.pb";
import type { UiReward, UiRewardProgramDetails, UiRewardSelectionState, UiRewardSlot } from "../rewardly/ui.pb";
import type { UiAlias } from "./UiAliasProto.pb";
import type { ClientScenario, Instrument, SharingContent } from "./api.pb";
import type { CardModule } from "./card_modules.pb";
import type { CardTheme as CardTheme1, TouchData } from "./cards.pb";
import type { CashInstrumentType } from "./cash_instrument_type.pb";
import type { DepositPreference } from "./deposit_preference.pb";
import type { CardScanResult } from "./experiments/bouncer.pb";
import type { ConversationStatus } from "./payment_conversation.pb";
import type { Reaction } from "./reaction.pb";
import type { BankAccountLinkingConfig, DisplayCountry, MenuItem, OAuthConfig } from "./scenarios.pb";
import type { ScheduledReloadData } from "./schedule_data.pb";
import type { StatusResult } from "./status_result.pb";
import type { UiControl } from "./ui/control.pb";
import type { UiCustomer } from "./ui/customer.pb";
import type { UiPayment } from "./ui/payment.pb";
import type { RewardSelection } from "./ui/reward_selection.pb";

export enum SmsDeliveryMechanism {
  SMS_TEXT = "SMS_TEXT",
  VOICE = "VOICE",
}

/** Reasons provided by the customer during identity verification. */
export enum UsageReason {
  PERSONAL = "PERSONAL",
  COMMERCIAL = "COMMERCIAL",
}

export enum FieldName {
  CARD_NUMBER = "CARD_NUMBER",
  CARD_EXPIRATION = "CARD_EXPIRATION",
  CARD_POSTAL_CODE = "CARD_POSTAL_CODE",
  /** PASSWORD - Unused. EMAIL_ADDRESS = 4; */
  PASSWORD = "PASSWORD",
  SMS_NUMBER = "SMS_NUMBER",
  SMS_VERIFICATION_CODE = "SMS_VERIFICATION_CODE",
  BANK_ROUTING_NUMBER = "BANK_ROUTING_NUMBER",
  BANK_ACCOUNT_NUMBER = "BANK_ACCOUNT_NUMBER",
  CARD_CVV = "CARD_CVV",
  EMAIL_VERIFICATION_CODE = "EMAIL_VERIFICATION_CODE",
}

export enum EnableCryptocurrencyTransferOutStatus {
  /** DISALLOWED - The customer is not allowed the enable transfers out. */
  DISALLOWED = "DISALLOWED",
  /** CAN_ENABLE - The customer is allowed to enable transfers out, but there are blockers to be satisfied first. */
  CAN_ENABLE = "CAN_ENABLE",
  /** PENDING_REVIEW - The customer has enabled transfers out, but their info still needs to be verified. */
  PENDING_REVIEW = "PENDING_REVIEW",
  /** ENABLED - The customer has enabled transfers out and can now initiate them. */
  ENABLED = "ENABLED",
  /**
   * MISMATCHED_IDV - The customer is allowed to enable transfers out, but their provided personal info doesn't
   * match the the info provided while becoming gold.
   */
  MISMATCHED_IDV = "MISMATCHED_IDV",
}

export enum EnableCryptocurrencyTransferInStatus {
  /** DEPOSITS_DISALLOWED - The customer is not allowed the enable transfers in. */
  DEPOSITS_DISALLOWED = "DEPOSITS_DISALLOWED",
  /** DEPOSITS_CAN_ENABLE - The customer is allowed to enable transfers in, but there are blockers to be satisfied first. */
  DEPOSITS_CAN_ENABLE = "DEPOSITS_CAN_ENABLE",
  /** DEPOSITS_PENDING_REVIEW - The customer has enabled transfers in, but their info still needs to be verified. */
  DEPOSITS_PENDING_REVIEW = "DEPOSITS_PENDING_REVIEW",
  /** DEPOSITS_ENABLED - The customer has enabled transfers in and can now initiate them. */
  DEPOSITS_ENABLED = "DEPOSITS_ENABLED",
  /**
   * DEPOSITS_MISMATCHED_IDV - The customer is allowed to enable transfers in, but their provided personal info doesn't
   * match the the info provided while becoming gold.
   */
  DEPOSITS_MISMATCHED_IDV = "DEPOSITS_MISMATCHED_IDV",
}

/**
 * For push notification to iOS apps, our server must use the appropriate certs when connecting to
 * the APNS service based on the type of app build.
 */
export enum BuildType {
  /** RELEASE - Release build (com.squareup.cash) of the Cash app. */
  RELEASE = "RELEASE",
  /** BETA - Beta build of the Cash app (NOTE: no longer used). */
  BETA = "BETA",
  /** DOGFOOD - Dogfood build (com.squareup.cash.dogfood) of the Cash app. */
  DOGFOOD = "DOGFOOD",
  /** ALPHA - Alpha build (com.squareup.cash.alpha) of the Cash app */
  ALPHA = "ALPHA",
}

export enum ExpirationPolicy {
  /**
   * NONE_DAY7 - Deprecated.
   *
   * @deprecated
   */
  NONE_DAY7 = "NONE_DAY7",
  /**
   * NONE_DAY30 - Deprecated.
   *
   * @deprecated
   */
  NONE_DAY30 = "NONE_DAY30",
  /**
   * DAY14_DAY14 - Deprecated.
   *
   * @deprecated
   */
  DAY14_DAY14 = "DAY14_DAY14",
  DAY2_DAY14 = "DAY2_DAY14",
  DAY7_DAY14 = "DAY7_DAY14",
  REFERRAL_BOUNTY = "REFERRAL_BOUNTY",
  YELLOW = "YELLOW",
  HOUR1_DAY14 = "HOUR1_DAY14",
  PAYROLL = "PAYROLL",
  WELCOME_BONUS = "WELCOME_BONUS",
  /**
   * BUSINESS - Deprecated.
   *
   * @deprecated
   */
  BUSINESS = "BUSINESS",
  REIMBURSEMENT = "REIMBURSEMENT",
  REWARD = "REWARD",
  IMESSAGE = "IMESSAGE",
  CAVIAR = "CAVIAR",
  CAVIAR_RESTAURANT = "CAVIAR_RESTAURANT",
  CAVIAR_COURIER = "CAVIAR_COURIER",
  CAVIAR_DINER = "CAVIAR_DINER",
  PURCHASE = "PURCHASE",
  CLAIMABLE = "CLAIMABLE",
  PAY_WITH_CASH = "PAY_WITH_CASH",
  INSTRUMENT_VERIFICATION = "INSTRUMENT_VERIFICATION",
  CROSS_BORDER = "CROSS_BORDER",
  COLLEGE_BONUS = "COLLEGE_BONUS",
  INVEST_TRADE_ERROR = "INVEST_TRADE_ERROR",
  MARKETING = "MARKETING",
  SUPPORT_BALANCE_TRANSFER = "SUPPORT_BALANCE_TRANSFER",
}

/** Each RecipientGroup value represents a set of customers that appears in the suggested list. */
export enum RecipientGroup {
  /** NEARBY - Customers that are nearby, discovered via BLE. */
  NEARBY = "NEARBY",
  /** RECENTS - Customers that the user has interacted with recently. */
  RECENTS = "RECENTS",
  /** SUGGESTIONS - Customers that are being suggested by the server. */
  SUGGESTIONS = "SUGGESTIONS",
}

export enum Platform {
  UNKNOWN = "UNKNOWN",
  ANDROID = "ANDROID",
  IOS = "IOS",
  WEB = "WEB",
}

export enum ClientApp {
  NOT_A_CLIENT_APP = "NOT_A_CLIENT_APP",
  CASH_APP = "CASH_APP",
}

export enum FeatureFlagSource {
  LAUNCH_DARKLY = "LAUNCH_DARKLY",
  AMPLITUDE = "AMPLITUDE",
}

/** The result of provisioning Apple Pay. */
export enum ApplePayProvisioningResult {
  SUCCESS = "SUCCESS",
  USER_CANCELED = "USER_CANCELED",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  INVALID_DATA_ERROR = "INVALID_DATA_ERROR",
  UNSUPPORTED_VERSION_ERROR = "UNSUPPORTED_VERSION_ERROR",
  INVALID_SIGNATURE = "INVALID_SIGNATURE",
  NOT_ENTITLED_ERROR = "NOT_ENTITLED_ERROR",
  /** ACTIVATION_FAILED - Only expected when activation occurs separate from provisioning (yellow flow). */
  ACTIVATION_FAILED = "ACTIVATION_FAILED",
  /**
   * PARTIAL_ACTIVATION - Only expected when activation occurs separate from provisioning (yellow flow). This will occur
   * when the request is for multiple cards and at least one card is activated and at least one
   * card fails to activate.
   */
  PARTIAL_ACTIVATION = "PARTIAL_ACTIVATION",
}

export enum CardTheme {
  BLACK = "BLACK",
  WHITE = "WHITE",
}

export enum FulfillmentProvider {
  PERFECT_PLASTIC = "PERFECT_PLASTIC",
  OBERTHUR = "OBERTHUR",
}

/**
 * This indicates the status of a client's session. It does not have a NONE option, because if the
 * client's session is invalid, the client should fail to get a ResponseContext, and likely would
 * get an HTTP 401 exception or a dedicated minimal response as in SignOutResponse.
 */
export enum SessionStatus {
  /**
   * PARTIAL - The session is currently in the process of onboarding/logging in and the customer is in a
   * dummy state.
   */
  PARTIAL = "PARTIAL",
  /**
   * FULL - The session is now fully logged in, even if there may be additional desired onboarding steps
   * to complete.
   */
  FULL = "FULL",
  /**
   * ANONYMOUS - The session is anonymous, does not link to any customer_token and is unable to authenticate unlike
   * FULL/PARTIAL token.
   */
  ANONYMOUS = "ANONYMOUS",
}

/** Actions that involve stored balances that involve system and customer limits. */
export enum LimitedAction {
  /**
   * ADD_CASH - The amount that a customer can add to their stored balance with a cash in, in their profile
   * currency.
   */
  ADD_CASH = "ADD_CASH",
  /** BUY_CRYPTOCURRENCY - The amount of cryptocurrency that a customer can buy, expressed in their profile currency. */
  BUY_CRYPTOCURRENCY = "BUY_CRYPTOCURRENCY",
  /** SELL_CRYPTOCURRENCY - The amount of cryptocurrency that a customer can sell, expressed in their profile currency. */
  SELL_CRYPTOCURRENCY = "SELL_CRYPTOCURRENCY",
}

export enum BitcoinDisplayUnits {
  /** BITCOIN - Display all BTC units in Bitcoin * */
  BITCOIN = "BITCOIN",
  /** SATOSHIS - Display all BTC units in Sats * */
  SATOSHIS = "SATOSHIS",
}

/**
 * Determines how a scenario was initiated on the device.
 * This can be used for analytics or to change behaviour of the scenario itself.
 * Note: This is similar to AppCreationActivity but meant to be generic for all scenarios.
 */
export enum ScenarioInitiatorType {
  /** EXTERNAL - Initiated by a different app (ex. a deep link) or platform service (ex. Siri). */
  EXTERNAL = "EXTERNAL",
  /** IN_APP_SCANNER - Initiated after the customer scanned a QR code using the in-app scanner. */
  IN_APP_SCANNER = "IN_APP_SCANNER",
  /** CARD_TAB_ACTIVATION_FLOW_SCANNER - Initiated after the customer scanned a QR code using the card tab scanner */
  CARD_TAB_ACTIVATION_FLOW_SCANNER = "CARD_TAB_ACTIVATION_FLOW_SCANNER",
}

/** Represents a debit or credit card & related information. */
export interface KeyedCard {
  /** The card's account number. */
  unencrypted_pan?: string;
  /** An expiration date in YYMM format. */
  expiration?: string;
  /** The card's CVV number. */
  security_code?: string;
  /** The postal code of the card's billing address. */
  postal_code?: string;
  /** The last four digits of the pan. */
  last_four?: string;
  /** The type of payment instrument, such as Visa or MasterCard. */
  instrument_type?: InstrumentType;
  /** The cardholder name, as reviewed & submitted by the customer. */
  cardholder_name?: string;
  /** The result of an OCR scan of a credit/debit card using the Bouncer SDK. */
  bouncer_scan_result?: CardScanResult;
}

/** Represents a bank account in a customer's profile. */
export interface BankAccount {
  /** The text to use when displaying the bank account to the user. e.g. "Bank of America 1234" */
  display_text?: string;
  /**
   * The URL of the icon to use when displaying the bank account. The icon might be the logo of a
   * specific bank or a generic icon.
   */
  icon_url?: string;
}

export interface Settings {
  email_address?: string;
  full_name?: string;
  card?: KeyedCard;
  /** True if the user has opted in to require their CVV to confirm each payment. */
  require_passcode_confirmation?: boolean;
  /** The type of payment instrument, such as Visa or MasterCard. Populated when card is populated. */
  instrument_type?: InstrumentType;
}

export interface InstrumentLinkingOption {
  /** The linking option's instrument type. */
  instrument_types?: CashInstrumentType[];
  /** The title for the linking option. */
  title?: string;
  /** The description of the linking option's purpose. */
  description?: string;
  /** The instrument option's associated fee. */
  fee_bps?: number;
  /** If this instrument should be shown in the instrument selector when drafting a payment. */
  show_in_instrument_selector?: boolean;
}

export interface DownloadableTaxForm {
  tax_year?: string;
  download_url?: string;
  form_title?: string;
}

/**
 * The BalanceData of Profile is DEPRECATED. We're migrating to SyncValues instead!
 * http://go/goodbye-profile
 */
export interface BalanceData {
  /**
   * The instruments supported for transfers.
   *
   * @deprecated
   */
  supported_transfer_instruments?: TransferInstrumentMap[];
  /** Whether the home screen pill with the customer's balance can be displayed. */
  cash_balance_home_screen_button_enabled?: boolean;
  /** The priority of the home screen pill with the customer's balance. */
  cash_balance_home_screen_button_priority?: number;
  /** Whether incoming transfers to add cash to the customer's balance are allowed. */
  adding_cash_enabled?: boolean;
  /** Exchange data for converting between currencies. */
  exchange_data?: ExchangeData;
  /**
   * Whether customer is allowed to make bitcoin deposits.
   *
   * @deprecated
   */
  bitcoin_deposit_enabled?: boolean;
  /**
   * Whether customer is allowed to make bitcoin withdrawals.
   *
   * @deprecated
   */
  bitcoin_withdrawal_enabled?: boolean;
  /** Whether the customer can enable cryptocurrency transfers out. */
  enable_cryptocurrency_transfer_out_status?: EnableCryptocurrencyTransferOutStatus;
  /** The text to use on the button for enabling cryptocurrency transfers out. */
  enable_cryptocurrency_transfer_out_button_text?: string;
  /** Whether the customer can enable cryptocurrency deposits. */
  enable_cryptocurrency_transfer_in_status?: EnableCryptocurrencyTransferInStatus;
  /** The text to use on the button for enabling cryptocurrency deposits. */
  enable_cryptocurrency_transfer_in_button_text?: string;
  /** The static limits on actions performed against a balance. */
  balance_limit_groups?: StaticLimitGroup[];
  /** The customer's current scheduled reload preference. This sets up a recurring Add Cash action. */
  scheduled_reload_data?: ScheduledReloadData;
  /**
   * Deprecated, Whether the customer is allowed to set up scheduled reloads. Use in ScheduledReloadData#supported instead.
   *
   * @deprecated
   */
  scheduled_reload_enabled?: boolean;
  /** The customer's current scheduled BTC buy preference. This sets up a recurring BTC buys. */
  scheduled_btc_buy_data?: ScheduledReloadData;
  /**
   * Deprecated, Whether the customer is allowed to set up scheduled BTC buys. Use ScheduledReloadData#supported instead.
   *
   * @deprecated
   */
  scheduled_btc_buy_enabled?: boolean;
  /** Whether the customer can deposit checks via camera capture */
  check_deposits_enabled?: boolean;
  /** Hint to clients on what action to perform when the Direct Deposit button is tapped. */
  direct_deposit?: BalanceDataButton;
  /** Hint to clients on what action to perform when Deposit Check button is tapped. */
  deposit_check?: BalanceDataButton;
  dda_form?: BalanceDataButton;
  bitcoin_p2p_enabled?: boolean;
}

export interface BalanceDataButton {
  action?: BalanceDataButtonAction;
  /** Must be non-null if action is DO_CLIENT_SCENARIO. */
  client_scenario?: ClientScenario;
  /** Must be non-null if action is SHOW_DIALOG. */
  dialog?: BalanceDataDialog;
  /** The text shown on the button. */
  text?: string;
  /** Localizable string for text field. Only present if BalanceData.Button is included in a sync-value. */
  localizable_text?: LocalizableString;
}

export enum BalanceDataButtonAction {
  DO_CLIENT_SCENARIO = "DO_CLIENT_SCENARIO",
  SHOW_DDA_COPY_AND_PASTE = "SHOW_DDA_COPY_AND_PASTE",
  SHOW_DIALOG = "SHOW_DIALOG",
}

export interface BalanceDataDialog {
  title?: string;
  localizable_title?: LocalizableString;
  /** The message shown on the dialog. */
  main_text?: string;
  localizable_main_text?: LocalizableString;
  /** The primary button, shown on the right, or top if vertically stacked. */
  primary_button?: BalanceDataDialogButton;
  /** The secondary button, shown on the left, or second if vertically stacked */
  secondary_button?: BalanceDataDialogButton;
  /** The tertiary button, shown on the bottom if vertically stacked. */
  tertiary_button?: BalanceDataDialogButton;
}

export interface BalanceDataDialogButton {
  /** The text shown on the button. */
  text?: string;
  localizable_text?: LocalizableString;
  /** The action to be taken when the button is tapped. */
  action?: BalanceDataDialogButtonAction;
  /** Must be non-null if ACTION is DO_CLIENT_SCENARIO. */
  client_scenario?: ClientScenario;
}

export enum BalanceDataDialogButtonAction {
  /** DO_CLIENT_SCENARIO - Proceed with the client scenario associated with the button. */
  DO_CLIENT_SCENARIO = "DO_CLIENT_SCENARIO",
  /** DISMISS - Dismisses the current context. */
  DISMISS = "DISMISS",
}

export interface LoyaltyData {
  /**
   * Deprecated. Whether or not we should show loyalty in activity.
   *
   * @deprecated
   */
  show_loyalty_in_activity?: boolean;
  /**
   * Deprecated. Whether or not the customer wishes to see notifications about Loyalty activity.
   *
   * @deprecated
   */
  loyalty_notifications_enabled?: boolean;
  /**
   * Deprecated. Whether or not to do database migration for ios.
   *
   * @deprecated
   */
  ios_database_reload_enabled?: boolean;
  /**
   * Deprecated. Whether or not loyalty-enabled scrolling quick access bar should be shown on iOS
   * clients.
   *
   * @deprecated
   */
  ios_show_scrolling_quick_access_bar?: boolean;
}

export interface ExchangeData {
  /** The base currency this exchange data represents. */
  base_currency_code?: CurrencyCode;
  /** Exchange rates from the base currency. */
  rates?: ExchangeRate[];
}

export interface ExchangeRate {
  /**
   * (deprecated) The market rate for a currency. The amount of market_value is the conversion
   * of one unit of currency defined in the base of ExchangeData.
   */
  market_value?: Money;
  /** (deprecated) The rate of change in basis points. */
  change_bps?: number;
  /**
   * The absolute rate of change in whole cents of the base currency from either 24 hours ago, or
   * based on the market opening price (if applicable).
   */
  change_cents?: number;
  /** The value in cents of the base currency per one standard unit of the currency. */
  base_value_cents?: number;
  /** The currency code of the currency. */
  currency_code?: CurrencyCode;
}

/**
 * A message containing pricing history for a specified time range, sample frequency, between a
 * currency pair.
 */
export interface PriceHistory {
  /**
   * The start time, in UTC Unix Epoch Time, of historical price samples. A NULL value should
   * be interpreted as: from ALL time.
   */
  start_time?: number;
  /**
   * The end time, in UTC Unix Epoch Time, of historical price samples. A NULL value should
   * be interpreted as: to PRESENT time.
   */
  end_time?: number;
  /** The time, in milliseconds, between samples. */
  tick_frequency_ms?: number;
  /** The base currency being measured. */
  base_currency_code?: string;
  /** The currency being quoted. */
  quote_currency_code?: string;
  /** The sampled data in ticks for rendering into a graph. */
  price_ticks?: PriceTick[];
}

/** A single price tick. */
export interface PriceTick {
  /** The time representated as UTC Unix Epoch Time. */
  time?: number;
  /** The number of cents, in the quote currency. */
  price_cents?: number;
}

/**
 * An exchange contract is a short-term option contract for a customer to buy or sell an exchangable
 * currency at a specific price, for a specific quanitity within a specific timeframe. This allows
 * for a better customer experience by not exposing the customer to any exchange volatility during
 * between the time they request the purchase or sale and the time they excute the contract.
 *
 * That said, to prevent gaming of the system, contracts expire at the specfied time, after which
 * the client must obtain a new contract to conduct a purchase or sale.
 */
export interface ExchangeContract {
  /** An opaque and unique token the client may use to reference this contract for execution. */
  contract_token?: string;
  /**
   * The amount of source currency that will be deducted from the customers balance in this currency
   * to perform the exchange.
   */
  source_amount?: Money;
  /**
   * The amount of the target currency that will be credited to the customers balance to perform
   * the exchange. (Note: This amount is based on the Square Composite Spot Rate plus or minus a
   * variable-rate spread, that insulates Square from exchange risk, and also encapsulastes our
   * fee for conducting the market marking.)
   */
  target_amount?: Money;
  /**
   * The date this contract expires, in Unix Epoch UTC. Controllable by the server, but something
   * on the order of 3-5 minutes since creation time.
   */
  expires_at?: number;
}

export interface TutorialData {
  /** When present, encapsulates data for showing the home screen pill tutorial. */
  home_screen_pill_tutorial?: TutorialItem;
  /** When present, encapsulates data for showing the balance drawer amount tutorial. */
  balance_drawer_amount_tutorial?: TutorialItem;
  /** When present, encapsulates data for showing the balance drawer issued card tutorial. */
  balance_drawer_issued_card_tutorial?: TutorialItem;
}

/** Encapsulates data for an overlay tutorial that highlights and points at an element on screen. */
export interface TutorialItem {
  /** The text to show as the header of the tutorial. e.g. "NEW" */
  header_text?: string;
  /** The main text of the tutorial. */
  main_text?: string;
  arrow_type?: TutorialItemArrowType;
  /** A version number for the tutorial. Apps should only show each version once. */
  version?: number;
}

export enum TutorialItemArrowType {
  /** CENTER_UP - The text is displayed below the element, and a centered arrow points straight up at it. */
  CENTER_UP = "CENTER_UP",
  /** CENTER_DOWN - The text is displayed above the element, and a centered arrow points straight down at it. */
  CENTER_DOWN = "CENTER_DOWN",
  /** RIGHT_UP - The text is displayed below the element, and a curved arrow points up on the right. */
  RIGHT_UP = "RIGHT_UP",
  /** LEFT_DOWN - The text is displayed above the element, and a curved arrow points down on the left. */
  LEFT_DOWN = "LEFT_DOWN",
}

export interface DepositPreferenceData {
  /**
   * The text to be shown when picking the preference during cashout. It may say something like
   * How would you like to deposit ${{amount}} to your bank? The template string {{amount}} will be
   * replaced by the clients with the actual amount.
   */
  cash_out_title?: string;
  /** Picker menu items in order which they will be displayed in account settings. */
  account_setting_options?: DepositPreferenceOption[];
  /** Picker menu items in order which they will be displayed during cash out. */
  cash_out_options?: DepositPreferenceOption[];
  /**
   * The default preference to set when toggling from RETAIN_FUNDS_IN_CASH_BALANCE. This preference
   * gets mapped to ClientScenarios. If this is null, set client scenario of ENABLE_AUTO_CASH_OUTS.
   */
  default_preference_option?: DepositPreference;
  /**
   * Deposit preference mapping to its fee.
   * This is deprecated. Use DepositPreferenceOption.fee_data instead.
   *
   * @deprecated
   */
  deposit_fee_data?: DepositFeeData;
  /** Determines whether Auto Cash Out toggle has to be rendered in the Customer Profile. */
  display_auto_cash_out_toggle?: boolean;
}

/** These are the picker options for deposit preference in settings/cashout. */
export interface DepositPreferenceOption {
  /** The deposit preference represented by this option. */
  deposit_preference?: DepositPreference;
  /** This has the fee information that is applied to this transaction. */
  fee_data?: FeeData;
  /** Text to be shown in the picker for this preference. */
  options_text?: string;
  /** Additional information about the options_text. */
  options_description_text?: string;
  /** Text to be shown in a confirmation sheet that slides up from the bottom. */
  confirmation_sheet_data?: ConfirmationSheetData;
  /**
   * When true, the app should suppress the fee when displaying this option, and also when
   * calculating the accepted_fee_cents for the associated cash out.
   */
  suppress_fee?: boolean;
}

export interface ConfirmationSheetData {
  /** The main text of the confirmation sheet. */
  main_text?: string;
  /** The text of the confirm/continue button. */
  confirm_button_text?: string;
  /** The text of the cancellation/back button. */
  cancel_button_text?: string;
}

/** Data relating to fees to depositing into a bank account. */
export interface DepositFeeData {
  /** The type of fee that the customer pays to deposit instantly. */
  fee_type?: DepositFeeDataFeeType;
  /**
   * The number of basis points of the fee. Only applies when the type is
   * BASIS_POINTS.
   */
  fee_bps?: number;
}

export enum DepositFeeDataFeeType {
  /** NONE - There is no fee. */
  NONE = "NONE",
  /** BASIS_POINTS - The fee is in basis points. */
  BASIS_POINTS = "BASIS_POINTS",
}

export interface FeeData {
  /** The number of basis points of the fee. */
  fee_bps?: number;
  /**
   * Minimum fee for the deposit. This fee is used when the variable fee (expressed by
   * the value of fee_bps * the amount being deposited + fee_fixed_amount_cents) is less than
   * this minimum fee.
   * This fee is expressed in cents.
   */
  minimum_fee_cents?: number;
  /** Fixed fee for the transaction. Defaults to 0. This fee is expressed in cents. */
  fee_fixed_amount_cents?: number;
  /**
   * Maximum fee for the deposit. This fee is used when the variable fee (expressed by
   * the value of fee_bps * the amount being deposited + fee_fixed_amount_cents) is greater
   * than this maximum fee.
   * This fee is expressed in cents.
   * Clients prior to 3.58 do not use this maximum natively and may display a larger fee than
   * the server is expected to ultimately charge.
   */
  maximum_fee_cents?: number;
}

/** Maps a supported source transfer instrument type to a list of target instrument types. */
export interface TransferInstrumentMap {
  /** @deprecated */
  source_instrument_token?: string;
  /** @deprecated */
  target_instrument_tokens?: string[];
  /** The instrument type which can be used as source for a transfer. */
  source_type?: CashInstrumentType;
  /** The instrument type which can be used as target for the above source_type. */
  target_types?: CashInstrumentType[];
}

export interface ProfileAlias {
  /**
   * The underlying alias represented by this profile alias. Only a subset of alias types are
   * supported, including SMS and EMAIL.
   */
  alias?: UiAlias;
  /**
   * True if the alias has been verified by the customer. Until an alias is verified, the customer
   * cannot receive payments sent to it.
   */
  verified?: boolean;
  /**
   * After expires_at profile alias will be expired on the client side, that way we don't need to
   * republish the sync value when it expires.
   */
  expires_at?: number;
  /**
   * The version of the alias for asynchronous updates. For example, when the alias is verified
   * a new version is published.
   * Events on the same alias occur in order of increasing versions.
   */
  version?: number;
  /**
   * The display_date of the alias for sorting aliases on the client side (app UI).
   * For unverified aliases, it is equal to challenge's createdAt time.
   * For verified aliases, it is equal to alias's linkedAt time.
   */
  display_date?: number;
}

/** A configuration record for managing reactions. */
export interface ReactionConfigRecord {
  /** The reaction id. NOTE: For INTERNAL use only. */
  id?: number;
  /** A meaningful name to describe this configuration record. Ex. #CashAppFriday or SuperBowl2018. */
  name?: string;
  type?: ReactionConfigRecordType;
  config_data?: ReactionConfigRecordConfigData;
  /** Whether this config is active. */
  active?: boolean;
  /** The schedule of when this configuration is applicable. */
  schedule?: Schedule;
  /** The country that this configuration applies to. Null means this is a universal config. */
  country?: Country;
  /**
   * When more than one config applies to a given day, the higher priority config is selected.
   * NOTE: This is used to relay the calculated priority value to the admin. It's not user provided nor persisted.
   */
  priority?: number;
}

/** The type of reaction configuration. Corresponds to franklin_main.reaction_configs::type database column. */
export enum ReactionConfigRecordType {
  PAYMENT = "PAYMENT",
  EXTENDED = "EXTENDED",
}

/** The configuration data itself (ie. Set of allowed reactions). */
export interface ReactionConfigRecordConfigData {
  /** The set of available reactions for this configuration. */
  reactions?: Reaction[];
}

export interface Schedule {
  /** The 4 digit calendar year this schedule applies to. If null, it applies to all years. */
  year?: number;
  /** The calendar month this schedule applies to. If null, it applies to all months. */
  month?: number;
  /** The day of the month this schedule applies to. If null, it applies to all days. */
  day_of_month?: number;
  /** The day of the week this schedule applies to. If null, it applies to all days of the week. */
  day_of_week?: number;
  /** The week of the month this schedule applies to. If null, it applies to all weeks of the month. */
  day_of_week_in_month?: number;
}

/** A client's view of a conversation. */
export interface Conversation {
  /** An opaque token used to identify this conversation. */
  conversation_token?: string;
  customers?: UiCustomer[];
  headline?: string;
  status?: ConversationStatus;
  /** Conversations should be sorted in descending order of sortValue. */
  sort_value?: number;
  /** The most recent date that a payment in the conversation was updated. */
  updated_at?: number;
  badge_count?: number;
}

/**
 * Contains UiPayments and their PaymentHistoryData for displaying an InitiatePaymentRequest or
 * TransferFundsRequest in the app's acitivity when it is offline and wasn't able to successfully
 * complete the request.
 */
export interface OfflinePaymentHistoryData {
  payments?: UiPayment[];
}

/**
 * CustomerVerificationData encapsulates the info for proving that the current customer is in fact
 * an existing customer.
 */
export interface CustomerVerificationData {
  /** An opaque token that identifies an existing customer. */
  token?: string;
  /**
   * The card (with the last four numbers and instrument type populated) belonging to the existing
   * customer. Provided so that the current customer can prove ownership of the card with its CVV.
   */
  card?: KeyedCard;
}

/**
 * A representation of a contact with email addresses and SMS numbers hashed to obfuscate their
 * actual values.
 */
export interface HashedContact {
  /**
   * A unique identifier for this contact from the caller's perspective. It is opaque to the
   * server. It cannot include PII.
   */
  contact_id?: string;
  /** A list of hashes of the contact's email addresses. */
  email_hashes?: string[];
  /** A list of hashes of the contact's SMS numbers. */
  sms_hashes?: string[];
  /**
   * The customer with customer id and photo url. Use its id to initiate payments and view
   * activity with this customer. The customer's email address and SMS number will NOT be
   * populated to prevent leaking PII.
   */
  customer?: UiCustomer;
  /** If the contact's aliases match multiple customers, additional customer IDs will be populated. */
  additional_customer_ids?: string[];
}

/** Information of a Cash customer who is synced to a device. */
export interface SyncedContact {
  hashed_alias?: Uint8Array;
  photo_url?: string;
  cashtag?: string;
  display_name?: string;
  can_accept_payments?: boolean;
  customer_token?: string;
  /** If true, this customer is a business (Cash Pro) customer. */
  is_business?: boolean;
  /** If true, the customer's display name was verified via Twitter or Facebook or manually by us. */
  is_verified_account?: boolean;
  /**
   * Fee to use when paying this customer by credit card. If null, use the default.
   * This allows for non-standard credit card rates for customers in specific states.
   */
  credit_card_fee_bps?: number;
  /** The country selected by the customer. */
  country_code?: Country;
  /** The date this customer joined Cash in milliseconds since 1/1/1970 UTC. */
  customer_joined_on?: number;
  /** If true, this customer is a Cash customer. */
  is_cash_customer?: boolean;
  hashed_alias_id?: string;
}

/** Server-driven configuration data relating to instrument linking in settings. */
export interface InstrumentLinkingConfig {
  /** The menu item for selecting to enter a debit card. */
  menu_item_card?: MenuItem;
  /** The menu item for selecting to enter a bank account by routing and account number. */
  menu_item_bank_account?: MenuItem;
  /** The menu item for selecting to enter a credit card. */
  menu_item_credit_card?: MenuItem;
  /** The menu item for enabling/disabling the Cash balance. */
  menu_item_cash_balance?: MenuItem;
  /** The header for linking an instrument when no instrument is linked and rate plan is BUSINESS. */
  instrument_header_no_instrument_linked?: string;
  /**
   * The description for linking an instrument when no instrument is linked and rate plan is
   * BUSINESS.
   */
  instrument_description_no_instrument_linked?: string;
  /**
   * The header for linking an instrument when a bank account is linked and the rate plan is
   * BUSINESS.
   */
  instrument_header_bank_account_linked?: string;
  /**
   * The description for linking an instrument when a bank account is linked and the rate plan is
   * BUSINESS.
   */
  instrument_description_bank_account_linked?: string;
  /** The header for linking an instrument when no instrument is linked and rate plan is PERSONAL. */
  instrument_header_no_instrument_linked_personal?: string;
  /**
   * The description for linking an instrument when no instrument is linked and rate plan is
   * PERSONAL.
   */
  instrument_description_no_instrument_linked_personal?: string;
  /** When true, the app should allow customers to link a credit card. Supported in v2.9+. */
  credit_card_linking_enabled?: boolean;
  /**
   * The maximum number of times to prompt a user that already has a debit card linked to enter
   * a credit card during payment flow.
   *
   * @deprecated
   */
  max_credit_card_prompts?: number;
  /**
   * The fee to use when paying a customer by credit card. Note that a UiCustomer can
   * override this fee for payments to that UiCustomer.
   */
  credit_card_fee_bps?: number;
  /** Whether the Cash balance is enabled. */
  cash_balance_enabled?: boolean;
  /**
   * Whether issued cards have launched for this customer. When true, the client should show the
   * card or a card unavailable message. When false, the client should never show an issued card.
   */
  issued_cards_enabled?: boolean;
  issued_card_disabled_style?: InstrumentLinkingConfigIssuedCardDisabledStyle;
  /**
   * Whether requesting a physical card for their Cash balance has launched for this customer. When
   * true, the client should show the flow for requesting/activating a physical card.
   */
  physical_issued_cards_enabled?: boolean;
  /** List of supported themes for physical cards. */
  supported_card_themes?: CardTheme[];
  /**
   * A sentinel instrument token used in contexts where confirmation or verification should ask
   * the customer to provide their customer passcode instead of the passcode of one of their cards.
   */
  customer_passcode_instrument_token?: string;
  /** The instrument types supported for transfers (in and out). */
  supported_transfer_instruments?: TransferInstrumentMap[];
  /** Whether linking accounts via Plaid is enabled. */
  bankbook_enabled?: boolean;
  /** Whether the app should allow (EMV) card linking via NFC. */
  nfc_card_linking_enabled?: boolean;
  /**
   * Configuration for linking a bank account via a third party OAuth flow. When this is present the client
   * should not call /link-bank-account. It should open a webview to the provided launch-url.
   * Then close the webview and return the URL contents when the webview redirects to the URL
   * matching the redir_regexp.
   *
   * Only valid when supported_instrument_types include the BANK_ACCOUNT.
   */
  bank_account_oauth_config?: OAuthConfig;
  /** Whether linking accounts via Yodlee is enabled. */
  yodlee_enabled?: boolean;
  /** Contain bank account linking preferences, will replace bank_account_oauth_config and yodlee_enabled eventually. */
  bank_account_linking_config?: BankAccountLinkingConfig;
}

export enum InstrumentLinkingConfigIssuedCardDisabledStyle {
  WHITE = "WHITE",
  GREY = "GREY",
}

/**
 * Server-driven configuration data pertaining to the list of process of displaying and selecting
 * recipients of a payment.
 */
export interface RecipientConfig {
  /** SuggestedRecipientsData to use when the user is sending cash. */
  pay_data?: SuggestedRecipientsData;
  /** SuggestedRecipientsData to use when the user is requesting cash. */
  request_data?: SuggestedRecipientsData;
  /**
   * When true, the app should ask the user to confirm adding a cashtag to the list of recipients
   * if that cashtag represents a customer that isn't in their contacts or activity.
   */
  confirm_cashtag_recipients?: boolean;
}

export interface SuggestedRecipientsData {
  suggested_customers?: UiCustomer[];
  /** An ordered list of recipient groups to display to the customer. */
  groups?: RecipientGroup[];
}

/** Server-driven configuration data relating to OCR. */
export interface OcrConfig {
  /** If true, allow cards to be linked using OCR. */
  enabled?: boolean;
  image_number?: number;
  /** The SDK key used for barcode scanning for DL verification. */
  manatee_works_sdk_key?: string;
  /** This allows us to send camera configs to the client dynamically when a new iPhone comes out. */
  camera_configurations?: OcrConfigCameraConfiguration[];
}

export interface OcrConfigCameraConfiguration {
  iphone_model?: string;
  driver_license_fixed_lens_position?: number;
  /** The light threshold to decide whether the phone will display the flash icon. */
  low_light_threshold?: number;
}

/** Server-driven configuration data relating to sharing your profile page. */
export interface SharingConfig {
  /** If true, show the share menu in settings. */
  enabled?: boolean;
  /** The content to share in settings. */
  sharing_content?: SharingContent;
}

/** Server-driven configuration data relating to the Apple Watch app. */
export interface AppleWatchConfig {
  /**
   * Text shown at the end of Apple Watch setup after the user grants permissions.
   * The text should suggest setting up a passcode.
   */
  after_granting_permissions_text?: string;
  /**
   * Text shown at the end of Apple Watch setup when the user has already granted permissions.
   * The text should suggest setting up a passcode.
   */
  permissions_already_granted_text?: string;
  /**
   * Normally true. Can be set to false to change the app such that push notifications become
   * optional instead of required to use the watch.
   */
  push_notifications_required?: boolean;
}

/** Server-driven configuration data relating to payment history. */
export interface PaymentHistoryConfig {
  /** The URL of the script for computing PaymentHistoryData for synced payments. */
  script_url?: string;
  /** The URL of the script for doing searches in activity. */
  search_script_url?: string;
  /**
   * A list of sentinel customer tokens that should be treated as My Cash in the threaded view of
   * activity. The app shows different statistics for My Cash vs a typical a customer.
   */
  transfer_customer_ids?: string[];
  /**
   * A list of sentinel customer tokens that should be treated as Banking/Card transactions in the
   * threaded view of activity. The app shows different statistics for these vs a typical customer.
   */
  banking_transaction_customer_ids?: string[];
  /**
   * A list of sentinel customer tokens that should be treated as Bitcoin transactions in the
   * threaded view of activity. The app shows different statistics for these vs a typical customer.
   */
  bitcoin_transaction_customer_ids?: string[];
  /**
   * A list of sentinel customer tokens that should be treated as Lending transactions in the
   * threaded view of activity.  The app shows different statistics for these vs a typical customer.
   */
  lending_transaction_customer_ids?: string[];
  /**
   * A list of sentinel customer tokens that should be treated as Referral related in the
   * threaded view of activity.  The app shows a different view for these vs a typical customer.
   */
  referral_customer_ids?: string[];
  /** Specified payment types to hide from the top level activity. */
  top_level_hidden_payment_types?: string[];
  /**
   * Payments with the specified payment types are not included in calculations of
   * whether a customer is a merchant with payment activity.
   * Used to differentiate between payments representing money movements and payments representing
   * loyalty activities (joining a loyalty program, earning points, etc).
   */
  loyalty_merchant_payment_activity_hidden_payment_types?: string[];
  /**
   * Payments types that belong to trade created by automated investing configurations.
   * For example, "AUTO_INVEST_ROUND_UP"
   */
  automated_investment_payment_types?: string[];
}

/** Server-driven list of Treehouse App configurations */
export interface TreehouseConfig {
  /** A customer specific list of available treehouse apps */
  apps?: TreehouseConfigTreehouseApp[];
}

/** The AppSpec for a treehouse application */
export interface TreehouseConfigTreehouseApp {
  /** The name of the app. */
  app_name?: string;
  /** The path of the root view of the app. */
  path?: string;
  /**
   * The manifestURL the app is hosted on.
   * In production environments this is managed by go/treelot.
   * In local development environments this should be set to:
   * cash-ios     http://localhost:8080
   * cash-android http://10.0.2.2:8080
   */
  manifestURL?: string;
}

/** The result of evaluating the availability of market capabilities for a customer */
export interface MarketCapabilitiesConfig {
  /** The list of all known capabilities and their availability */
  capabilities?: MarketCapability[];
}

/** Server-driven configuration data relating to blockers. */
export interface BlockersConfig {
  /**
   * When true, the app should use a single input field for addresses and display choices based on
   * matches using the Google Places API.
   */
  address_typeahead_enabled?: boolean;
  /** Fields to be used when Add Cash was initiated by the action on a StatusResultButton. */
  add_cash_header_text?: string;
  target_balance_amount?: Money;
}

/** Server-driven configuration data relating to offline app behaviour. */
export interface OfflineConfig {
  /**
   * Whether or not offline mode is allowed. If enabled then clients will attempt to upload
   * payments and transfers in the background if those items failed due an offline client or
   * server outage.
   */
  enabled?: boolean;
  /** The URI of an external service to query for Franklin Service Status Messages. */
  external_status_url?: string;
  /**
   * The intervals, in milliseconds, at which to retry requests that failed due to a
   * communication issue with the server.
   *
   * The intervals are relative to each other, with the first one relative to the initial
   * time of the request. For example, intervals of [5000, 10000] would indicate that the
   * request should first be retried 5s after the initial attempt failed, and the second
   * retry should be made 10s after the first retry failed (and no less than 15s from when
   * the initial request failed).
   */
  retry_intervals?: number[];
  /**
   * The status shown to the user when a payment is sent, but fails due to communication
   * issues with the server.
   * The strings may contain the {{amount}} token.
   *
   * Example:
   *  Text      : "Your {{amount}} payment will be sent shortly."
   *  Promo Text: "Cash is having communication issues. We'll notify you when this payment
   * is sent."
   */
  attempted_payment_status_result?: StatusResult;
  /**
   * The status shown to the user when a payment was not sent because the client is offline.
   * The strings may contain the {{amount}} token.
   *
   * Example:
   *  Text      : "Your {{amount}} payment is ready to send."
   *  Promo Text: "We'll send this payment when you are connected to the Internet."
   */
  offline_payment_status_result?: StatusResult;
  /**
   * The status shown to the user when a request for money (bill) is sent, but fails due to
   * communication issues with the server.
   * The strings may contain the {{amount}} token.
   *
   * Example:
   *  Text      : "Your {{amount}} request will be sent shortly."
   *  Promo Text: "Cash is having communication issues. We'll notify you when this request
   * is sent."
   */
  attempted_bill_status_result?: StatusResult;
  /**
   * The status shown to the user when a request for money (bill) was not sent because the client
   * is offline.
   * The strings may contain the {{amount}} token.
   *
   * Example:
   *  Text      : "Your {{amount}} request is ready to send."
   *  Promo Text: "We'll send this request when you are connected to the Internet."
   */
  offline_bill_status_result?: StatusResult;
  /**
   * The status shown to the user when an attempt is made to cash out, but it fails due to
   * communication issues with the server.
   *
   * Example:
   *  Text: "Cash is having communication issues. We'll complete this deposit shortly and notify you
   * when finished."
   */
  attempted_cash_out_status_result?: StatusResult;
  /**
   * The status shown to the user when an attempt is made to cash out, but it was not sent
   * because the client is offline.
   *
   * Example:
   *  Text: "We'll complete this deposit when you are connected to the Internet."
   */
  offline_cash_out_status_result?: StatusResult;
  /**
   * The status shown to the user when an attempt is made to add cash to a stored balance,
   * but it fails due to communication issues with the server.
   *
   * Example:
   *  Text: "Cash is having communication issues. We'll add this cash to your account shortly and notify
   * you when it's complete."
   */
  attempted_add_cash_status_result?: StatusResult;
  /**
   * The status shown to the user when an attempt is made to add cash to a stored balance,
   * but it fails because the client is offline.
   *
   * Example:
   *  Text: "We'll add this cash to your account when you are connected to the Internet."
   */
  offline_add_cash_status_result?: StatusResult;
}

export interface Stamp {
  /** An unique identifier for the stamp. */
  name?: string;
  /** A human-friendly name that can be used for accessibility purposes. */
  accessibility_text?: string;
  /** The SVG representation of the stamp. */
  svg?: string;
  /** The minimum scale for the stamps, for instance 80 would mean 80% of default size. */
  min_scale?: number;
}

/** Server-driven configuration data relating to stamps. */
export interface StampsConfig {
  /** Collection of available stamps. */
  stamps?: Stamp[];
}

/** Server-driven configuration data relating to cryptocurrency balances. */
export interface CryptocurrencyConfig {
  /** The main text of the welcome dialog. */
  btc_welcome_message?: string;
  /** The text and url for the button that will redirect to our more info page. */
  learn_about_btc_button_text?: string;
  learn_about_btc_url?: string;
  /** The text for the button to dismiss the welcome dialog. */
  dismiss_button_text?: string;
  /** The minimum cryptocurrency withdrawal limit in Sats * */
  minimum_withdrawal_limit_sats?: number;
}

/** Server-driven configuration data relating to Cash Card drawer. */
export interface CashDrawerConfig {
  get_card_asset?: CashDrawerConfigGetCardAsset;
}

export interface CashDrawerConfigGetCardAsset {
  video_url?: string;
  image_url?: string;
  width?: number;
  height?: number;
}

/** Server-driven configuration data relating to Boost. */
export interface BoostConfig {
  selection_screen_title?: string;
}

/** Status of the reward program for a given customer for use in the app. */
export interface RewardStatus {
  /** Whether to allow reward code entry from the profile screen. */
  code_entry_enabled?: boolean;
  /** The minimum length a valid reward code can be, provided if code entry is enabled. */
  minimum_code_length?: number;
  /** Whether a button for the reward program on the home screen should be displayed. */
  reward_screen_enabled?: boolean;
  /** Text for the button on the home screen. */
  reward_button_text?: string;
  /** Priority for the button on the home screen, in case other buttons are available for the space. */
  reward_button_priority?: number;
  /** Text for the header in the reward screen accessed via the home screen. */
  reward_header_text?: string;
  /** Text for the reward screen. */
  reward_main_text?: string;
  /** How many reward payments have already been obtained. */
  completed_reward_payments?: number;
  /** How many reward payments the customer can still get. */
  available_reward_payments?: number;
  reward_payment_amount?: Money;
  /** A guide to the treatment of the reward screen depending on the state of the expiration. */
  expiration?: RewardStatusExpiration;
}

export enum RewardStatusExpiration {
  VALID = "VALID",
  EXPIRING = "EXPIRING",
}

/** What the app should display about a just created payment. */
export interface PaymentRewardStatus {
  /** Text to show if present. */
  main_text?: string;
  /** If true, payments should be displayed using information in RewardStatus. */
  show_payments?: boolean;
}

export interface AppVersion {
  platform?: Platform;
  major_version?: number;
  minor_version?: number;
  patch_version?: number;
}

/**
 * Data that the client passes to javascript. For example, 2.14 apps support both conversation
 * style and flat activity, the app passes in a flag to indicate to the payment history javascript
 * which style is in use.
 */
export interface ClientData {
  /** True when activity is displayed as a flat list. Introduced for 2.14. */
  flat_activity?: boolean;
  /**
   * True when phrase-based receipts are displayed. This was never released as the design
   * was superseded by the details-sheet style receipt.
   */
  phrase_receipt_details?: boolean;
  /** True when the details-sheet style receipts are displayed. */
  details_sheet_receipts?: boolean;
  /** Feature Flags */
  feature_flags?: FeatureFlag[];
  /** Current Time */
  current_time_millis?: number;
  /**
   * WARNING: This field is inverted from the conventional offset. For example, San Francisco PST is
   * UTC-08:00, which suggests this should be -480 minutes. But due to a historical blunder this
   * field uses the negated value, positive 480 minutes.
   */
  current_timezone_offset_minutes?: number;
  /** Preferred Bitcoin Display Units */
  bitcoin_display_units?: BitcoinDisplayUnits;
  /** Locale in a format of `en-US` * */
  locale?: string;
}

export interface IssuedCard {
  /** A token identifying this issued card. */
  token?: string;
  /** The card's number. */
  pan?: string;
  /** An expiration date in YYMM format. */
  expiration?: string;
  /** The card's CVV number. */
  security_code?: string;
  /** The last four digits of the pan. */
  last_four?: string;
  /** The type of payment instrument, such as Visa or MasterCard. */
  instrument_type?: InstrumentType;
  /** The name of the cardholder. Right now, this will always be the customer's full name. */
  cardholder_name?: string;
  /** True if the customer has completed activation, which must occur before first use. */
  activated?: boolean;
  /** When false, the card will always be declined if attempted to be used for a payment. */
  enabled?: boolean;
  /** True if the card is a virtual card. False if the card is a physical card. */
  virtual?: boolean;
  /**
   * True if the caller must verify a passcode or passcode token to view the card number,
   * expiration and CVV.
   */
  locked?: boolean;
  /** True if this card has been issued as an emergency-use card during an outage. */
  emergency?: boolean;
  /**
   * Used to identify an Apple Pay pass on a local device if non-null.
   * If null, clients should suppress Add to Apple Pay UI.
   */
  apple_pay_identifier?: string;
  /** Description of the card when used in Apple Pay. */
  apple_pay_description?: string;
  /**
   * An opaque token for this issued card. If a client has persisted an issued card, it should
   * send down this token to the server which can save the server from making unnecessary calls
   * to Marqeta, and possibly alert the client to drop its persisted card.
   */
  version_token?: string;
  /** Data containing information about the customer's physical card. */
  physical_card_data?: PhysicalCardData;
  /** The digital wallet tokens associated with this issued card */
  digital_wallet_tokens?: DigitalWalletToken[];
  /** The card theme associated with this issued card */
  card_theme?: CardTheme1;
  /**
   * Whether this is a sponsored card. This field was never used.
   *
   * @deprecated
   */
  sponsored?: boolean;
  card_status_text?: string;
  localizable_card_status_text?: LocalizableString;
  dimmed?: boolean;
  /**
   * Whether this card has been locked by a sponsor.
   * https://docs.google.com/document/d/1TR5wTRy1LHS1Js8N-QgS2zjcbcsdwwrNgXb2GiTWJ54/edit?usp=sharing
   */
  sponsor_locked?: boolean;
  issuer?: IssuedCardIssuer;
  issued_at?: number;
  /** Tags are out of order. Next tag = 27; */
  touch_data?: TouchData;
}

export enum IssuedCardIssuer {
  /** SHAZAM - there will be new issuer in the future */
  SHAZAM = "SHAZAM",
  MARQETA = "MARQETA",
  VISA_DPS = "VISA_DPS",
}

export interface DirectDepositAccount {
  /** The routing number for the DDA. */
  routing_number?: string;
  /** The account number. If the account is redacted, this may not be provided. */
  account_number?: string;
  /** The prefix of the account number, provided if the account is redacted. */
  account_number_prefix?: string;
  /** True if the customer doesn't have a DDA and the numbers provided are for display purposes only. */
  is_placeholder?: boolean;
  /**
   * Describes what functionality is available with DDAs. If not null, to be displayed below DDA
   * buttons.
   */
  dda_explanation_text?: string;
  localizable_dda_explanation_text?: LocalizableString;
  /** Display complete account number to the user. */
  display_complete_account_number?: boolean;
  /** The support node token to navigate to that explains more about direct deposit accounts. */
  support_node_token?: string;
  /** The account state for a customer's primary DDA. */
  primary_dda_state?: DirectDepositAccountDirectDepositAccountState;
}

export enum DirectDepositAccountDirectDepositAccountState {
  OPEN = "OPEN",
  CLOSED = "CLOSED",
  NOT_PRESENT = "NOT_PRESENT",
  LOCKED = "LOCKED",
}

/** A SettingsGroup to be display for a specific balance drawer. */
export interface StaticLimitGroup {
  /** The currency code for the balance drawer the group is tied to. */
  currency?: CurrencyCode;
  balance_settings_group?: SettingsGroup;
}

/** A collection of SettingsCategory that should be displayed together under a common title. */
export interface SettingsGroup {
  /** The text to use for the header. */
  title?: string;
  /** A list of various categories to display. */
  settings_categories?: SettingsCategory[];
}

/** A collection of items to display together in the app under a common header. */
export interface SettingsCategory {
  /** Display text for this category, e.g. "Send", or "Withdraw" */
  header?: string;
  /**
   * One or more display strings describing the limit e.g. ["Unlimited"], ["$2,500 per week"]
   * or ["$2,500 per day", "$10,000 per week"]. Multiple items will be displayed on multiple lines.
   */
  items?: string[];
  /** One or more ProgressBars describing this limit. */
  progress_bars?: SettingsCategoryProgressBar[];
}

/**
 * Represents a horizontal progress bar, with primary (left) and secondary (right) text.
 * Both the secondary text and the progress bar itself can be hidden by leaving the relevant
 * fields null/unset.
 */
export interface SettingsCategoryProgressBar {
  /**
   * Shown to the left of the progress bar. Must not be null.
   * e.g. "$5,432.10 left" or "Unlimited"
   */
  primary_display_text?: string;
  /** Shown to the right of the progress bar. Can be null. e.g. "$1,000 per week" */
  secondary_display_text?: string;
  /**
   * Progress to show in the bar, range [0, 1]. To hide the progress bar and just show the labels,
   * leave this field null/unset.
   */
  progress?: number;
}

/**
 * A setting which modifies the behavior of the app. Many feature flags map to server-side
 * experiments.
 */
export interface FeatureFlag {
  /**
   * The programmatic name of this feature flag. For feature flags stored on the server,
   * this corresponds to the experiment name.
   */
  name?: string;
  /** A human readable description of this feature flag. */
  label?: string;
  /**
   * The options available for this feature flag. This list will be empty when served in profile.
   * Use GetFeatureFlagsRequest to get the full list of feature flags with options.
   */
  options?: FeatureFlagOption[];
  /**
   * The currently selected option. To change the selected option, change this value and add
   * the modified feature flag to the SetFeatureFlagsResponse.
   */
  selected_option_value?: string;
  /** True if this feature flag should be included in ClientData.feature_flags. */
  client_data_enabled?: boolean;
  /** True if this feature flag is an experiment. */
  is_experiment?: boolean;
  /**
   * Denotes where feature flags have been configured.
   * This field will be used by clients to emit different exposure events.
   */
  feature_flag_source?: FeatureFlagSource;
}

/** Each option describes a different way that the client should behave. */
export interface FeatureFlagOption {
  /**
   * The programmatic name of this option. For feature flags stored on the server, this
   * corresponds to the experiment bucket.
   */
  value?: string;
  /** A human readable description of this option. */
  label?: string;
}

export interface ClientDisplayTheme {
  /**
   * This is just a starting point for theme information - this should expand as we get a better
   * sense of what is required.
   */
  base_color?: string;
  accent_color?: string;
}

/** Configuration for apps authenticating Cash links to the web via one-time use tokens. */
export interface WebLoginConfig {
  /** A token that replaces {{token}} in URLs. */
  token?: string;
  expires_at?: number;
}

/** Server-driven data for sharing Cashtag QR codes to Instagram Stories. */
export interface InstagramShareQRData {
  /** Route compiles QR code image, display name and Cashtag into an Instagram sticker image. */
  image_url?: string;
  /** The top color hex of the story background gradient. */
  top_color?: string;
  /** The bottom color hex of the story background gradient. */
  bottom_color?: string;
}

export interface ThreadedViewMetric {
  /** The label of the metric. e.g. "Payments" or "Sent". */
  label?: string;
  amount?: Money | undefined;
  count?: number | undefined;
}

export interface ThreadedViewData {
  metrics?: ThreadedViewMetric[];
  header?: string;
  subheader?: string;
  photo_url?: string;
}

export interface PhysicalCardData {
  physical_card_state?: PhysicalCardDataState;
}

export enum PhysicalCardDataState {
  /** REQUESTED - A physical card has been requested but not shipped. */
  REQUESTED = "REQUESTED",
  /** READY_TO_ACTIVATE - The physical card has been shipped. */
  READY_TO_ACTIVATE = "READY_TO_ACTIVATE",
  /** ACTIVATED - The physical card has been activated. */
  ACTIVATED = "ACTIVATED",
}

export interface CashDrawerData {
  /**
   * Deprecated in favour of card_controls.
   * Last Android Client version to use it: 2.54
   *
   * @deprecated
   */
  main_controls?: UiControl[];
  /**
   * Deprecated in favour of card_controls.
   * Last Android Client version to use it: 2.54
   *
   * @deprecated
   */
  overflow_controls?: UiControl[];
  /**
   * If the client uses card_controls to update/render the card drawer, main_controls and
   * overflow_controls should not be used.
   */
  card_controls?: CashDrawerDataCardControls;
  /**
   * An indicator to bypass client-side logic around null state handling (for example showing the
   * "order a free cash card" upsell animation) and display the data provided here
   * in the card profile instead.
   */
  skip_null_state?: boolean;
}

export interface CashDrawerDataCardControls {
  card_control?: UiControl;
  card_status_control?: UiControl;
  overflow_controls?: UiControl[];
}

/**
 * CardScheme represents a top level container for UI modules, to be displayed on the CashApp card tab.
 * Each module is in turn a container of elements.
 */
export interface CardScheme {
  modules?: CardModule[];
}

export interface RoundUpsData {
  /** Whether the round-ups feature is active/inactive etc. */
  status?: AutomationAutomationStatus;
  /** Info about the round-ups investment target (eg. company name). */
  target?: AutomationAutomationTarget;
}

/**
 * A concise representation of information about the device/app that the user is
 * using; it is essentially a combination of RequestContext, ClientInfo & BrowserInteraction.
 *
 * Sent between systems to propagate up to date information about the user & their
 * device for the *current* request, e.g. for risk systems.
 *
 * Note. Most of these fields can be `null`, if we don't have that information for that particular request.
 */
export interface ClientContext {
  timestamp?: number;
  user_agent?: string;
  /** Best-guess estimate of the platform that this device is running on, possibly sniffed from the UA. */
  platform?: Platform;
  client_ip?: string;
  tracking_cookie?: string;
  /** Device token for iOS Device Check integration. */
  device_check_token?: Uint8Array;
}

export interface RetryContext {
  /**
   * The timestamp that the payment/transfer was originally attempted to be created at.
   * This should correspond to the time that the original request is sent.
   */
  created_at?: number;
  /** The attempt number. Starts at 1 and increases with each attempt by the app. */
  retry_attempt?: number;
  /** A list of the customer's linked instruments at the original creation time of the payment. */
  instruments?: Instrument[];
  /**
   * True for a foreground retry (i.e. user-initiated refresh), false (the default) for a background
   * retry (i.e. app-scheduled retry). This distinction is used, for example, to prevent sending
   * notifications for foreground retries.
   */
  is_foreground?: boolean;
}

export interface RewardsData {
  /** All possible rewards that are either selectable or selected for this customer */
  rewards?: UiReward[];
  /** Reward program details. */
  program_details?: UiRewardProgramDetails[];
  slots?: UiRewardSlot[];
  /** Tokens pointing to rewards that are selectable */
  selectable_rewards_tokens?: string[];
  reward_selection_states?: UiRewardSelectionState[];
  /**
   * Additional information about the reward selections that will be passed back as a result
   * of explicit customer interaction with Boosts (add/replace/remove Boost, opt into a locked
   * Boost challenge, etc.)
   */
  reward_selections?: RewardSelection[];
  /**
   * Only show rewards in the app when this bit is set
   *
   * @deprecated
   */
  show_rewards?: boolean;
  /**
   * This flag is only set if we want to show boost video over other videos in the card drawer where
   * there is no card. This flag can be set for customers with or without card. Clients would have
   * to check for card existence separately.
   *
   * @deprecated
   */
  show_boost_video?: boolean;
  /**
   * Indicates to the clients whether or not they should defer invoking AddOrUpdateReward to
   * allow a customer without a Cash card to interact with the Boost selection screen.
   *
   * @deprecated
   */
  defer_reward_selection?: boolean;
  /** This flag is set to true if the customer has never selected a reward. */
  new_to_boost?: boolean;
  /**
   * Ordered list of tokens similar to `selectable_rewards_tokens` with the inclusion of the
   * selected reward.
   */
  ordered_reward_tokens?: string[];
  /**
   * Set to true if the user has used a boost at least in a recent period of time (
   * defaults to 90 days).
   * Field is used to render conditionally render text for offers tab upsell view.
   * This field is only populated if the user is eligible to see offers tab.
   */
  has_recently_used_boost?: boolean;
}

export interface CountrySelectionData {
  /** A list of countries the customer can select. */
  display_countries?: DisplayCountry[];
  /** The currently selected country for customer. */
  country_code?: Country;
}

/**
 * Used for in-app service status messages. Published to S3 for consumption by apps
 * in situations when the service is unhealthy.
 */
export interface ServiceMessage {
  title?: string;
  message?: string;
  icon_url?: string;
  more_info_url?: string;
}

export interface DigitalWalletToken {
  /** The issuer of this digital wallet token. */
  issuer?: DigitalWalletTokenIssuer;
  /**
   * The reference of the pan - e.g. the FPAN Id for Apple Pay. This may be associated with
   * multiple instances of adding the card to wallets.
   */
  pan_reference_id?: string;
  state?: DigitalWalletTokenState;
}

export enum DigitalWalletTokenIssuer {
  APPLE_PAY = "APPLE_PAY",
  ANDROID_PAY = "ANDROID_PAY",
  UNKNOWN_ISSUER = "UNKNOWN_ISSUER",
}

export enum DigitalWalletTokenState {
  /** REQUESTED - This is the most basic state, we know about the TAR but nothing else has occurred */
  REQUESTED = "REQUESTED",
  /** VERIFICATION_REQUESTED - Apple has requested additional verification before provisioning */
  VERIFICATION_REQUESTED = "VERIFICATION_REQUESTED",
  /** REJECTED - Apple, Visa or Marqeta has rejected this DWT TAR */
  REJECTED = "REJECTED",
  /** SUSPENDED - A once active DWT is currently suspended */
  SUSPENDED = "SUSPENDED",
  /** ACTIVE - DW is active and ready for use */
  ACTIVE = "ACTIVE",
  /** REJECTED_INTERNALLY - We rejected this DWT TAR */
  REJECTED_INTERNALLY = "REJECTED_INTERNALLY",
  /** FAILED - We attempted to activate but the request failed */
  FAILED = "FAILED",
  /** TERMINATED - The DWT has been terminated and is permanently non-functional */
  TERMINATED = "TERMINATED",
  /** REQUEST_DECLINED - The DWT request has been declined */
  REQUEST_DECLINED = "REQUEST_DECLINED",
}

/**
 * Heuristics from device data used to guess the customer's location.
 * Clients should populate this on a best effort basis. All fields are optional.
 */
export interface DeviceLocationHeuristics {
  /** A 2 letter language code like "en". */
  language?: string;
  /** A 2 letter country code like "us". */
  country_code?: string;
  /** A carrier name like "T-Mobile". */
  carrier_name?: string;
  /** A 2 letter country code like "us". */
  carrier_country_code?: string;
  /** A time zone descriptor like "America/Chicago". */
  time_zone?: string;
  /** A list of keyboards installed on the device, like "en_US". */
  installed_keyboards?: string[];
}

export interface DigitalWalletTokenProvisioningRequestData {
  digital_wallet_issuer?: DigitalWalletTokenIssuer;
  apple_pay_request?: DigitalWalletTokenProvisioningRequestDataApplePayRequest | undefined;
  google_pay_request?: DigitalWalletTokenProvisioningRequestDataGooglePayRequest | undefined;
}

export interface DigitalWalletTokenProvisioningRequestDataApplePayRequest {
  /** An array of data for encrypted certificates used to provision an Apple Pay instrument */
  certificates?: Uint8Array[];
  /** The nonce data provided by the client for this request */
  nonce?: Uint8Array;
  /** The nonce signature provided by client for this request */
  nonce_signature?: Uint8Array;
}

export interface DigitalWalletTokenProvisioningRequestDataGooglePayRequest {
  /** The user's Google wallet account ID. */
  wallet_account_id?: string;
  /** The user's Android device ID; the device's unique identifier. */
  device_id?: string;
}

export interface DigitalWalletTokenProvisioningResponseData {
  digital_wallet_issuer?: DigitalWalletTokenIssuer;
  apple_pay_response?: DigitalWalletTokenProvisioningResponseDataApplePayResponse | undefined;
  google_pay_response?: DigitalWalletTokenProvisioningResponseDataGooglePayResponse | undefined;
  status?: DigitalWalletTokenProvisioningResponseDataStatus;
}

export enum DigitalWalletTokenProvisioningResponseDataStatus {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export interface DigitalWalletTokenProvisioningResponseDataApplePayResponse {
  /** Contains sensitive information needed by Apple to add the card to Apple Pay */
  encrypted_pass_data?: Uint8Array;
  /** Contains data provided to the payment network as a one-time-pad (OTP) */
  activation_data?: Uint8Array;
  /** The ephemeral public key used by elliptic curve cryptography */
  ephemeral_public_key?: Uint8Array;
}

export interface DigitalWalletTokenProvisioningResponseDataGooglePayResponse {
  /** Blob of info Google Pay can provide to the Token Service Provider to receive a token */
  opaque_payment_card?: string;
}

export interface DigitalWalletTokenProvisioningCompletionData {
  digital_wallet_issuer?: DigitalWalletTokenIssuer;
  provisioning_result?: DigitalWalletTokenProvisioningCompletionDataProvisioningResult;
}

/** The result of provisioning a digital wallet token. */
export enum DigitalWalletTokenProvisioningCompletionDataProvisioningResult {
  SUCCESS = "SUCCESS",
  USER_CANCELED = "USER_CANCELED",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  INVALID_DATA_ERROR = "INVALID_DATA_ERROR",
  UNSUPPORTED_VERSION_ERROR = "UNSUPPORTED_VERSION_ERROR",
  INVALID_SIGNATURE = "INVALID_SIGNATURE",
  NOT_ENTITLED_ERROR = "NOT_ENTITLED_ERROR",
  /** ACTIVATION_FAILED - Only expected when activation occurs separate from provisioning (yellow flow). */
  ACTIVATION_FAILED = "ACTIVATION_FAILED",
  /**
   * PARTIAL_ACTIVATION - Only expected when activation occurs separate from provisioning (yellow flow). This will occur
   * when the request is for multiple cards and at least one card is activated and at least one
   * card fails to activate.
   */
  PARTIAL_ACTIVATION = "PARTIAL_ACTIVATION",
  /** NO_ACTIVE_WALLET - There is no active wallet. Google Pay specific. */
  NO_ACTIVE_WALLET = "NO_ACTIVE_WALLET",
  /**
   * TOKEN_NOT_FOUND - The indicated issuer token id does not match a token in the active wallet.
   * This status can be returned by calls to the Google Pay SDK that specify an issuer token id.
   * Google Pay specific.
   */
  TOKEN_NOT_FOUND = "TOKEN_NOT_FOUND",
  /**
   * INVALID_TOKEN_STATE - The specified token was found but was not in a legal state for the operation to succeed.
   * For example, this can happen when attempting to select as default a token that is not in
   * the state TOKEN_STATE_ACTIVE. Google Pay specific.
   */
  INVALID_TOKEN_STATE = "INVALID_TOKEN_STATE",
  /**
   * ATTESTATION_ERROR - Tokenization failed because the device did not pass a compatibility check.
   * Google Pay specific.
   */
  ATTESTATION_ERROR = "ATTESTATION_ERROR",
  /** TAP_AND_PAY_UNAVAILABLE - The TapAndPay API cannot be called by the current application. Google Pay specific. */
  TAP_AND_PAY_UNAVAILABLE = "TAP_AND_PAY_UNAVAILABLE",
}
